import React, { Fragment, useState } from 'react';
import { FieldArray, Formik, getIn } from 'formik';
import { isEmpty, startCase } from 'lodash';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import swal from 'sweetalert';
import { object, string, number, array, boolean } from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import Autocomplete from 'react-google-autocomplete';
import TextField from '../../components/UI/TextField';
import { POST } from '../../services/rest.service';
import AppLoader from '../../components/loaders/AppLoader';
import convertJsonToFormData from '../../helpers/helper';
import config from '../../../config';
import { hideLoading, showLoading } from '../../redux/loader/LoaderActions';
import { showModal } from '../../redux/error/ErrorAction';
import { formatCreateUpdateJobInitVals } from '../../helpers/utils';
import { acceptedFileTypes, OTHER_OPTION } from '../../helpers/constants';
import DocumentModal from '../../components/Modals/DocumentModal';
import ErrorMessage from '../../components/global/ErrorMessage';

/**
 * The characters a typed Terminal or Shipping Line name may contain.
 *
 * Deliberately the same set as ReferenceName::FREE_TEXT_PATTERN on the API, so
 * the importer is told here rather than by a 422 after submitting.
 */
const FREE_TEXT_RX = /^[A-Za-z0-9 &.,\-'/()]+$/;

const CreateServices = (props) => {
  const serviceData = useSelector((state) => state.enumData);
  const dispatch = useDispatch();
  const [documentToShow, setDocumentToShow] = useState('');

  const formatLabel = (label) => {
    if (!label) return '';
    if (label.toLowerCase() === 'roro') return 'RoRo';
    return startCase(label);
  };
  const showDoc = (docc) => setDocumentToShow(docc);

  const createBothServices = (values, jobId = null) => {
    const payload = {
      category: values.category,
      type: values.category === '1' ? values.vehical_type : '',

      no_of_containers:
        values.category === '1'
          ? values.roroNoOfcontainer
          : values.category === '2'
          ? values.generalNoOfContainer
          : '',

      type_of_cargo: values.category === '1' ? '' : values.generalTypeOfcargo,
      single_item_title:
        values.category === '1' || values.category === '2'
          ? values.containers
              .map((c) => c.item_name)
              .filter(Boolean)
              .join(', ')
          : values.generalItemName,
      paar_link: values.category === '1' ? values.roroPAAR : values.generalPAAR || '',

      lading_link: values.category === '1' ? values.roroBillLading : values.generalBillLading || '',

      date_of_arrival: moment(values.dateOfArrival).format('YYYY-MM-DD'),
      containers:
        values.category === '1'
          ? values.containers.map((c) => ({
              ...c,
              item_name: c.item_name || '',
              load_description: c.load_description || '',
              vehicles: c.vehicles.map((v) => ({
                ...v,
                size_of_container: c.size_of_container,
                container_type: c.container_type,
                description: c.load_description || c.item_name || '',
              })),
            }))
          : values.category === '2'
          ? values.containers.map((c) => ({
              ...c,
              item_name: c.item_name || '',
              size_of_container: c.size_of_container,
              container_type: c.container_type,
              load_description: c.item_name || c.load_description || '',
              vehicles: [
                {
                  size_of_container: c.size_of_container,
                  container_type: c.container_type,
                  item_name: c.item_name || '',
                  description: c.item_name || c.load_description || '',
                  container_uuid: c.id,
                  id: c.id,
                },
              ],
            }))
          : [],
      has_forwarding: values.forwardingJob ? '1' : '0',
      port_of_discharge: values.forwardingLocation || '',
      destination: values.forwardingJob ? values.forwardingDestination : '',
      destination_state: values.forwardingJob ? values.forwardingDestinationState : '',
      tonnage: values.category === '3' ? values.bulkTonnage : '',
      performa_invoice: values.performa_invoice,
      clearing_discharge_port: values.clearingDischarge,
      // Only meaningful on a clearing job. Sent empty on a transport-only job
      // so the API's nullable rules clear rather than reject them.
      // "Others" is a form-only sentinel. It is split here into the uuid
      // field or the free-text field, never both — the API keeps one source of
      // truth per side and would clear whichever it was not given.
      terminal_uuid:
        values.clearingJob && values.terminal !== OTHER_OPTION ? values.terminal || '' : '',
      terminal_other:
        values.clearingJob && values.terminal === OTHER_OPTION ? values.terminalOther || '' : '',
      shipping_line_uuid:
        values.clearingJob && values.shippingLine !== OTHER_OPTION
          ? values.shippingLine || ''
          : '',
      shipping_line_other:
        values.clearingJob && values.shippingLine === OTHER_OPTION
          ? values.shippingLineOther || ''
          : '',
      mode_of_release: values.clearingJob ? values.modeOfRelease || '' : '',
    };

    // this status is required for forwarding only job
    if (!values.clearingJob && values.forwardingJob) {
      payload.clearing_status = 11;
    }

    let URL = '/jobs/create';

    if (jobId) {
      URL = '/jobs/update';
      payload.job_uuid = jobId;
      // remove the paar & bill landing if they are not changed during update
      if (
        typeof payload.paar_link === 'string' &&
        payload.paar_link.startsWith('storage/documents/')
      ) {
        delete payload.paar_link;
      }
      // Send the Bill of Lading only when the importer actually picked a new
      // file. Anything else — an empty value, or a path left over from a
      // previous load — is not a file and would fail the `mimes` rule, failing
      // the whole save over a document the importer never touched.
      //
      // Tests for a File rather than for a path prefix: the document now lives
      // on a private disk and its path is not serialised onto the job at all,
      // so there is no prefix left to recognise.
      if (!(payload.lading_link instanceof File)) {
        delete payload.lading_link;
      }
      if (
        typeof payload.performa_invoice === 'string' &&
        payload.performa_invoice.startsWith('storage/documents/')
      ) {
        delete payload.performa_invoice;
      }
    }

    const formData = convertJsonToFormData(payload);
    // console.log('>> payload',payload , formData);

    dispatch(showLoading());

    POST(URL, formData)
      .then(({ data }) => {
        dispatch(
          showModal(jobId ? 'Job Updated succssefully!' : 'Request created succssefully!', true),
        );
        dispatch(hideLoading());
        if (jobId) {
          props.history.push('/client/jobs');
        } else {
          props.history.goBack();
        }
      })
      .catch((err) => {
        dispatch(showModal(err.message));
        dispatch(hideLoading());
      });
  };

  if (isEmpty(serviceData)) {
    return <AppLoader />;
  }
  // Logic for Updating the Job
  const jobDetails = props.location.state;
  // console.log('<<<< jobDetails >>>>', jobDetails);
  const formInitValues = formatCreateUpdateJobInitVals(jobDetails);
  // console.log('<<<< formInitValues >>>>', formInitValues);

  return (
    <div id="tabblock2" className="tabcontent">
      <div id="outcont">
        <section className="container" style={{ minHeight: '66vh' }}>
          <section className="bodycont create-service-form">
            <Formik
              initialValues={formInitValues}
              onSubmit={(values, errors) => {
                swal({
                  title: 'Undertaking',
                  text:
                    'By proceeding with this job request, you hereby undertake that your cargo is wholly and correctly declared. Any error or omission in cargo declaration will result in additional charges.',
                  icon: 'warning',
                  buttons: ['Cancel', 'Proceed'],
                }).then((result) => {
                  if (result) {
                    const jobId = values.jobId;
                    createBothServices(values, jobId);
                  }
                });
              }}
              validationSchema={object().shape({
                // NOTE: commented this since we are supporting only forwarding job also
                // clearingJob: boolean().when('forwardingJob', {
                //   is: true,
                //   then: boolean().required('Please select Job Type, Clearing Job is Mandatory'),
                // }),

                // forwardingJob: boolean().when(['clearingJob', 'roroType'], {
                //   is: (cj, rrt) => {
                //     return cj === true && rrt === true;
                //   },
                //   then: boolean().required('Please select Job Type'),
                // }),

                jobType: boolean().equals([true], 'Atleast One Job Type is required!'),
                category: string().required('Please Select Cargo Category'),

                vehical_type: string().when('vehicleType', {
                  is: true,
                  then: string().required('Please Select Vehicle Type'),
                }),

                containers: array().when(
                  ['category', 'vehical_type', 'roroType'],
                  (category, vehicalType, roroType, schema) => {
                    if (String(category) !== '1' || !roroType) return schema;

                    const vehicleSchema = object().shape({
                      brand: string().required('Brand is required'),
                      model: string().required('Model is required'),
                      year: string().required('Year is required'),
                      vehicle_state: string().required('Condition is required'),
                      vreg_no: string().nullable(),
                    });

                    if (String(vehicalType) === '2') {
                      return schema
                        .of(
                          object().shape({
                            roroNoOfvechile: number()
                              .required('Number of Vehicle is required')
                              .min(1, 'Number of Vehicle must be atleast 1'),
                            size_of_container: string().required('Size of Container is required'),
                            container_type: string().required('Container Type is required'),
                            load_description: string().nullable(),
                            vehicles: array()
                              .of(vehicleSchema)
                              .min(1, 'Vehicle details are required'),
                          }),
                        )
                        .min(1, 'Container is required');
                    }

                    return schema
                      .of(
                        object().shape({
                          vehicles: array()
                            .of(vehicleSchema)
                            .min(1, 'Vehicle details are required'),
                          size_of_container: string().required('Size of Container is required'),
                          container_type: string().required('Container Type is required'),
                        }),
                      )
                      .min(1, 'Vehicle details are required');
                  },
                ),
                roroNoOfcontainer: string().when('roroType', {
                  is: true,
                  then: string().required('No of container is required'),
                }),
                dateOfArrival: string().required('Expected Date of Arrival is Required'),
                // for roro only PAAR is required
                // and for generalContainerType there is another field generalPAAR so this is not required
                // roroPAAR: string().when(['category', 'vehical_type', 'generalContainerType'], {
                //   is: (cat, vt, gct) => (gct ? false : !(cat === '1' && vt === '1')),
                //   then: string().required('Please upload PAAR document'),
                // }),

                // forwardingLocation: string().when('forwardingJob', {
                //   is: true,
                //   then: string().required('Please select location'),
                // }),
                // forwardingDestination: string().when('forwardingJob', {
                //   is: true,
                //   then: string().required('Please select destination'),
                // }),

                generalNoOfContainer: number().when('ContainerType', {
                  is: true,
                  then: number().required('No of Container is Required'),
                }),
                generalTypeOfcargo: string().when('generalContainerType', {
                  is: true,
                  then: string().required('Please Select Cargo Type'),
                }),
                generalItemName: string().when(
                  ['generalContainerType', 'category'],
                  (generalContainerType, category, schema) => {
                    if (generalContainerType && String(category) === '3') {
                      return schema.required('Item name is required');
                    }
                    return schema.nullable();
                  },
                ),

                containers: array().when(
                  ['category', 'generalType'],
                  (category, generalType, schema) => {
                    if (String(category) === '2' && generalType) {
                      return schema
                        .of(
                          object().shape({
                            item_name: string().required('Item Name is required'),
                            size_of_container: string().required('Size of Container is required'),
                            container_type: string().required('Container Type is required'),
                          }),
                        )
                        .min(1, 'At least one container is required');
                    }
                    return schema;
                  },
                ),

                // generalPAAR: string().when('generalContainerType', {
                //   is: true,
                //   then: string().required('Please upload PAAR'),
                // }),

                bulkTonnage: string().when(
                  ['generalBulkCargoType', 'category'],
                  (generalBulkCargoType, category, schema) => {
                    if (generalBulkCargoType && String(category) === '3') {
                      return schema.required('Tonnage is required');
                    }
                    return schema;
                  },
                ),

                // performa_invoice: string().when('forwardingJob', {
                //   is: true,
                //   then: string().required('Please upload Proforma Invoice'),
                // }),

                clearingDischarge: string().when('clearingJob', (clearingJob, schema) => {
                  if (clearingJob) {
                    schema.required('Please Select Port of Discharge');
                  }
                  return schema.nullable();
                }),

                /**
                 * Terminal and Shipping Line are how an agent prices the work,
                 * so a clearing job cannot go out to bidding without them. The
                 * operator is blocked from approving until they are set
                 * (JobService::operatorApproveJob), so asking here saves the
                 * job bouncing back to the importer.
                 *
                 * Deliberately NOT expressed as "these or a Bill of Lading".
                 * Making the two conditional on each other is a cyclic
                 * dependency, which Yup throws on while building the schema —
                 * that would take the whole form down, not just the field.
                 */
                terminal: string().when('clearingJob', (clearingJob, schema) => {
                  if (clearingJob) return schema.required('Please select Terminal');
                  return schema.nullable();
                }),

                shippingLine: string().when('clearingJob', (clearingJob, schema) => {
                  if (clearingJob) return schema.required('Please select Shipping Line');
                  return schema.nullable();
                }),

                /*
                 * Required only when the select is on "Others". Depends on
                 * `terminal`, which depends on nothing, so there is no cycle
                 * for Yup to throw on.
                 *
                 * The character rule mirrors ReferenceName::FREE_TEXT_PATTERN
                 * on the API. It is a convenience, not the guard — the server
                 * refuses the same input whether or not this runs.
                 */
                terminalOther: string().when('terminal', (terminal, schema) => {
                  if (terminal !== OTHER_OPTION) return schema.nullable();
                  return schema
                    .required('Please enter the terminal name')
                    .min(2, 'Please enter at least 2 characters')
                    .max(120, 'Please use 120 characters or fewer')
                    .matches(
                      FREE_TEXT_RX,
                      "Only letters, numbers, spaces and & . , - ' / ( ) are allowed",
                    );
                }),

                shippingLineOther: string().when('shippingLine', (shippingLine, schema) => {
                  if (shippingLine !== OTHER_OPTION) return schema.nullable();
                  return schema
                    .required('Please enter the shipping line name')
                    .min(2, 'Please enter at least 2 characters')
                    .max(120, 'Please use 120 characters or fewer')
                    .matches(
                      FREE_TEXT_RX,
                      "Only letters, numbers, spaces and & . , - ' / ( ) are allowed",
                    );
                }),

                /**
                 * Required only when posting. Jobs created before this field
                 * existed hold null, and forcing a choice on them would block
                 * the importer from saving any other edit to an older job.
                 */
                modeOfRelease: string().when(
                  ['clearingJob', 'jobId'],
                  (clearingJob, jobId, schema) => {
                    if (clearingJob && !jobId) {
                      return schema.required('Please select Mode of Release');
                    }
                    return schema.nullable();
                  },
                ),
              })}
            >
              {(renderProps) => {
                const {
                  values,
                  touched,
                  errors,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  setFieldValue,
                  setValues,
                } = renderProps;
                // console.log('FORM values >>>>>', { values, touched, errors });
                // conditions for which there is no forwarding job
                let hasNoForwardingJob = false;
                if (values.category == 3 && values.generalTypeOfcargo === '1') {
                  hasNoForwardingJob = true;
                }
                if (values.vehical_type === '1' && values.category === '1') {
                  hasNoForwardingJob = true;
                }

                return (
                  <form onSubmit={handleSubmit}>
                    <div>{values.clearingJob}</div>
                    <div className="container">
                      <h2>Service Request</h2>

                      <div className="payment-info">
                        <ul
                          className="nav nav-tabs responsive justify-content-center checkbox-cls"
                          id="nonResponsiveTabs"
                          style={{ marginTop: '20px', marginBottom: '20px' }}
                        >
                          <li className="checkbox-inner-cls">
                            <input
                              type="checkbox"
                              className="inner-box"
                              id="clearing"
                              // defaultChecked
                              onClick={(e) => e.preventDefault()}
                              onChange={() => {
                                setValues({
                                  ...values,
                                  clearingJob: true,
                                  jobType: true,
                                });
                              }}
                              checked={values.clearingJob ? true : false}
                            />
                            <label className="deco-none red-class" htmlFor="clearing">
                              {' '}
                              Clearing Job
                            </label>
                          </li>
                          <li className="tooltip-container">
                            <input
                              type="checkbox"
                              id="fowarding"
                              className="inner-box"
                              disabled
                              onChange={() => {
                                const fields = {
                                  forwardingJob: !values.forwardingJob,
                                  jobType: !values.forwardingJob || values.clearingJob,
                                };
                                setValues({
                                  ...values,
                                  ...fields,
                                });
                              }}
                              checked={values.forwardingJob ? true : false}
                            />
                            <label className="deco-none red-class" htmlFor="fowarding">
                              {' '}
                              Transport Job
                              <span className="tooltip-text">
                                Transport Service Currently Unavailable
                              </span>
                            </label>
                          </li>
                          {getIn(touched, 'jobType') && getIn(errors, 'jobType') && (
                            <div className="invalid-feedback mb-3 display-block">
                              {getIn(errors, 'jobType')}
                            </div>
                          )}
                        </ul>

                        <div className="row">
                          {/* Clearing Form */}
                          {/* <h3 className="text-lg-start">Clearing Services</h3> */}

                          <div className="col-sm-12 text-left">
                            <label>
                              Select Cargo Category <span className="text-danger">*</span>
                            </label>

                            <select
                              className="form-control"
                              value={values.category}
                              onChange={(e) => {
                                const value = e.target.value;
                                const fields = {
                                  category: value,
                                  generalTypeOfcargo: '',
                                };

                                if (value === '1') {
                                  fields.vehicleType = true;
                                  fields.roroType = true;
                                  fields.vehical_type = '';
                                  fields.generalType = false;
                                  fields.vehicalContainerType = false;
                                  fields.generalContainerType = false;
                                  fields.ContainerType = false;
                                  fields.generalBulkCargoType = false;
                                } else if (value === '2') {
                                  fields.vehicleType = false;
                                  fields.roroType = false;
                                  fields.generalType = true;
                                  fields.vehicalContainerType = false;
                                  fields.generalContainerType = true;
                                  fields.ContainerType = true;
                                  fields.generalBulkCargoType = false;
                                } else if (value === '3') {
                                  fields.vehicleType = false;
                                  fields.roroType = false;
                                  fields.generalType = false;
                                  fields.vehicalContainerType = false;
                                  fields.generalContainerType = true;
                                  fields.ContainerType = false;
                                  fields.generalBulkCargoType = true;
                                }
                                setValues({
                                  ...values,
                                  ...fields,
                                });
                              }}
                              id="category"
                              onBlur={handleBlur}
                            >
                              <option value="">Select</option>
                              {Object.keys(serviceData.job.category).map((key) => (
                                <option value={key} key={key}>
                                  {formatLabel(serviceData.job.category[key])}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* <!-- Vehicle --> */}
                          {values.vehicleType && (
                            <div className="col-sm-12 text-left">
                              <label>
                                Select Cargo Type <span className="text-danger">*</span>
                              </label>

                              <select
                                className="form-control"
                                value={values.vehical_type || ''}
                                onBlur={handleBlur}
                                id="vehical_type"
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const fields = {};
                                  if (value == 1) {
                                    const containersreset = [
                                      {
                                        roroNoOfvechile: 0,
                                        vehicles: [
                                          {
                                            brand: '',
                                            model: '',
                                            year: '',
                                            id: uuidv4(),
                                            vehicle_state: '',
                                            vreg_no: '',
                                          },
                                        ],
                                        id: uuidv4(),
                                        load_description: '',
                                        size_of_container: '',
                                        container_type: '',
                                      },
                                    ];
                                    fields.containers = containersreset;
                                    // setting forwarding false because we don't support this type of job
                                    fields.forwardingJob = false;
                                    fields.jobType = true;
                                  }
                                  fields.vehical_type = value;
                                  fields.roroType = true;
                                  fields.vehicalContainerType = false;
                                  setValues({
                                    ...values,
                                    ...fields,
                                  });
                                }}
                              >
                                <option value="">Select</option>
                                {Object.keys(serviceData.job.vehicle_type).map((key) => (
                                  <Fragment key={key}>
                                    {key !== '3' ? (
                                      <option value={key} key={key}>
                                        {formatLabel(serviceData.job.vehicle_type[key])}
                                      </option>
                                    ) : (
                                      ''
                                    )}
                                  </Fragment>
                                ))}
                              </select>
                            </div>
                          )}
                          {/* <!-- RORO --> */}
                          {values.roroType && values.vehical_type && (
                            <Fragment>
                              {values.vehical_type == 2 && (
                                <div className="col-sm-6 text-left">
                                  <label>
                                    Number of Containers <span className="text-danger">*</span>
                                  </label>

                                  <TextField
                                    value={values.roroNoOfcontainer}
                                    id="roroNoOfcontainer"
                                    name="roroNoOfcontainer"
                                    onChange={(e) => {
                                      const value = e.target.value ? parseInt(e.target.value) : '';
                                      const fields = {
                                        roroNoOfcontainer: value,
                                      };

                                      if (value >= 1) {
                                        let tempContainer = [];
                                        for (let i = 0; i < value; i++) {
                                          tempContainer.push({
                                            roroNoOfvechile: 0,
                                            vehicles: [],
                                            item_name: '',
                                            size_of_container: '',
                                            container_type: '',
                                            id: uuidv4(),
                                          });
                                        }
                                        fields.containers = tempContainer;
                                      } else {
                                        fields.containers = [];
                                      }
                                      setValues({
                                        ...values,
                                        ...fields,
                                      });
                                    }}
                                    onBlur={handleBlur}
                                    error={
                                      getIn(touched, 'roroNoOfcontainer') &&
                                      getIn(errors, 'roroNoOfcontainer')
                                    }
                                    type="number"
                                    className="form-control"
                                    placeholder="Number of Containers"
                                    min="0"
                                    onWheel={(e) => e.target.blur()} // to prevent value change when mouse wheel scroll over this field
                                  />
                                </div>
                              )}

                              <div className="col-sm-6 text-left">
                                <label>
                                  Expected Date of Arrival <span className="text-danger">*</span>
                                </label>
                                <TextField
                                  type="date"
                                  className="form-control"
                                  placeholder=" "
                                  value={values.dateOfArrival}
                                  name="dateOfArrival"
                                  id="dateOfArrival"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={
                                    getIn(touched, 'dateOfArrival') &&
                                    getIn(errors, 'dateOfArrival')
                                  }
                                  min={moment().format('YYYY-MM-DD')}
                                />
                              </div>
                              <hr />
                              <FieldArray
                                name="containers"
                                render={(arrayHelpers) => (
                                  <>
                                    {values.containers.map((containerData, index) => (
                                      <Fragment key={index}>
                                        {values.vehical_type == 2 && (
                                          <label
                                            className="text-left"
                                            style={{
                                              color: '#00828d',
                                              fontWeight: '600',
                                              borderBottom: '1px solid #eee',
                                              paddingBottom: '10px',
                                              display: 'block',
                                            }}
                                          >{`Container No. ${index + 1}`}</label>
                                        )}

                                        {values.vehical_type === '1' &&
                                        values.category === '1' ? null : (
                                          <>
                                            <div className="col-sm-6 text-left">
                                              <label>
                                                Size of Container{' '}
                                                <span className="text-danger">*</span>
                                              </label>
                                              <select
                                                className="form-control"
                                                value={containerData.size_of_container}
                                                name={`containers.${index}.size_of_container`}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                              >
                                                <option value="">Select Size</option>
                                                <option value="20ft">20ft</option>
                                                <option value="40ft">40ft</option>
                                                <option value="45ft">45ft</option>
                                              </select>
                                              <p
                                                style={{ fontSize: '.875em' }}
                                                className="text-danger"
                                              >
                                                <ErrorMessage
                                                  name={`containers.${index}.size_of_container`}
                                                />
                                              </p>
                                            </div>

                                            {/* Size and type are separate questions: a 40ft box can be Dry
                                                or Reefer, and each type carries its own tariff. */}
                                            <div className="col-sm-6 text-left">
                                              <label>
                                                Container Type <span className="text-danger">*</span>
                                              </label>
                                              <select
                                                className="form-control"
                                                value={containerData.container_type || ''}
                                                name={`containers.${index}.container_type`}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                              >
                                                <option value="">Select Type</option>
                                                <option value="Dry">Dry</option>
                                                <option value="Flat rack">Flat rack</option>
                                                <option value="High cube">High cube</option>
                                                <option value="OOG">OOG</option>
                                                <option value="Reefer">Reefer</option>
                                              </select>
                                              <p style={{ fontSize: '.875em' }} className="text-danger">
                                                <ErrorMessage name={`containers.${index}.container_type`} />
                                              </p>
                                            </div>

                                            <div className="col-sm-6 text-left">
                                              <label>
                                                No of Vehicles{' '}
                                                <span className="text-danger">*</span>
                                              </label>

                                              <TextField
                                                type="number"
                                                className="form-control"
                                                placeholder="vechile"
                                                value={containerData.roroNoOfvechile}
                                                min="0"
                                                name={`containers.${index}.roroNoOfvechile`}
                                                onChange={(e) => {
                                                  const value = e.target.value
                                                    ? parseInt(e.target.value)
                                                    : '';
                                                  setFieldValue(
                                                    `containers.${index}.roroNoOfvechile`,
                                                    value,
                                                  );
                                                  if (value >= 1) {
                                                    let tempVechile = [];
                                                    for (let i = 0; i < value; i++) {
                                                      tempVechile.push({
                                                        brand: '',
                                                        model: '',
                                                        year: '',
                                                        id: uuidv4(),
                                                        vehicle_state: '',
                                                        vreg_no: '',
                                                      });
                                                    }
                                                    setFieldValue(
                                                      `containers.${index}.vehicles`,
                                                      tempVechile,
                                                    );
                                                  } else {
                                                    setFieldValue(
                                                      `containers.${index}.vehicles`,
                                                      [],
                                                    );
                                                  }
                                                }}
                                                // onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={
                                                  getIn(touched, 'vechile') &&
                                                  getIn(errors, 'vechile')
                                                }
                                                onWheel={(e) => e.target.blur()} // to prevent value change when mouse wheel scroll over this field
                                              />
                                              <p
                                                style={{ fontSize: '.875em' }}
                                                className="text-danger"
                                              >
                                                <ErrorMessage
                                                  name={`containers.${index}.roroNoOfvechile`}
                                                />
                                              </p>
                                            </div>
                                            {!(
                                              values.vehical_type === '1' && values.category === '1'
                                            ) && (
                                              <div className="col-sm-4 text-left">
                                                <label>Description of Added Items </label>
                                                <TextField
                                                  type="text"
                                                  className="form-control"
                                                  placeholder="Description"
                                                  value={containerData.load_description}
                                                  name={`containers.${index}.load_description`}
                                                  onChange={handleChange}
                                                  onBlur={handleBlur}
                                                  error={
                                                    getIn(touched, 'load_description') &&
                                                    getIn(errors, 'load_description')
                                                  }
                                                />
                                                <p
                                                  style={{ fontSize: '.875em' }}
                                                  className="text-danger"
                                                >
                                                  <ErrorMessage
                                                    name={`containers.${index}.load_description`}
                                                  />
                                                </p>
                                              </div>
                                            )}
                                          </>
                                        )}
                                        <FieldArray
                                          name={`containers.${index}.vechile`}
                                          render={(arrayHelpers) => (
                                            <>
                                              {values.containers[index].vehicles?.map(
                                                (vechileData, index1) => (
                                                  <Fragment key={index1}>
                                                    {values.vehical_type == 2 && (
                                                      <label className="text-left vehicle-label">{`Vehicle No ${index1 +
                                                        1}`}</label>
                                                    )}
                                                    <div className="col-sm-4 text-left">
                                                      <label>
                                                        Brand <span className="text-danger">*</span>
                                                      </label>

                                                      <TextField
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Brand"
                                                        value={vechileData.brand}
                                                        name={`containers.${index}.vehicles.${index1}.brand`}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={
                                                          getIn(touched, 'brand') &&
                                                          getIn(errors, 'brand')
                                                        }
                                                      />
                                                      <p
                                                        style={{ fontSize: '.875em' }}
                                                        className="text-danger"
                                                      >
                                                        <ErrorMessage
                                                          name={`containers.${index}.vehicles.${index1}.brand`}
                                                        />
                                                      </p>
                                                    </div>

                                                    <div className="col-sm-4 text-left">
                                                      <label>
                                                        Model <span className="text-danger">*</span>
                                                      </label>
                                                      <TextField
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Model"
                                                        value={vechileData.model}
                                                        name={`containers.${index}.vehicles.${index1}.model`}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={
                                                          getIn(touched, 'model') &&
                                                          getIn(errors, 'model')
                                                        }
                                                      />
                                                      <p
                                                        style={{ fontSize: '.875em' }}
                                                        className="text-danger"
                                                      >
                                                        <ErrorMessage
                                                          name={`containers.${index}.vehicles.${index1}.model`}
                                                        />
                                                      </p>
                                                    </div>
                                                    <div className="col-sm-4 text-left">
                                                      <label>
                                                        Year <span className="text-danger">*</span>
                                                      </label>

                                                      <select
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Year"
                                                        value={vechileData.year}
                                                        name={`containers.${index}.vehicles.${index1}.year`}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={
                                                          getIn(touched, 'year') &&
                                                          getIn(errors, 'year')
                                                        }
                                                      >
                                                        <option>Select Year</option>
                                                        {serviceData.year_list &&
                                                          serviceData.year_list.length &&
                                                          serviceData.year_list.map((year) => (
                                                            <option value={year} key={year}>
                                                              {year}
                                                            </option>
                                                          ))}
                                                      </select>
                                                      <p
                                                        style={{ fontSize: '.875em' }}
                                                        className="text-danger"
                                                      >
                                                        <ErrorMessage
                                                          name={`containers.${index}.vehicles.${index1}.year`}
                                                        />
                                                      </p>
                                                    </div>
                                                    <div className="col-sm-4 text-left">
                                                      <label>
                                                        Condition{' '}
                                                        <span className="text-danger">*</span>
                                                      </label>

                                                      <select
                                                        className="form-control"
                                                        value={vechileData.vehicle_state}
                                                        name={`containers.${index}.vehicles.${index1}.vehicle_state`}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={
                                                          getIn(touched, 'vehicle_state') &&
                                                          getIn(errors, 'vehicle_state')
                                                        }
                                                      >
                                                        <option value="">Select Condition</option>
                                                        {Object.keys(
                                                          serviceData.job.vehicle_state,
                                                        ).map((key) => (
                                                          <option value={key} key={key}>
                                                            {formatLabel(
                                                              serviceData.job.vehicle_state[key],
                                                            )}
                                                          </option>
                                                        ))}
                                                      </select>
                                                      <p
                                                        style={{ fontSize: '.875em' }}
                                                        className="text-danger"
                                                      >
                                                        <ErrorMessage
                                                          name={`containers.${index}.vehicles.${index1}.vehicle_state`}
                                                        />
                                                      </p>
                                                    </div>
                                                    <div className="col-sm-4 text-left">
                                                      <label>VREG Number</label>
                                                      <TextField
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="VREG Number"
                                                        value={vechileData.vreg_no}
                                                        name={`containers.${index}.vehicles.${index1}.vreg_no`}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={
                                                          getIn(touched, 'vreg_no') &&
                                                          getIn(errors, 'vreg_no')
                                                        }
                                                      />
                                                      <p
                                                        style={{ fontSize: '.875em' }}
                                                        className="text-danger"
                                                      >
                                                        <ErrorMessage
                                                          name={`containers.${index}.vehicles.${index1}.vreg_no`}
                                                        />
                                                      </p>
                                                    </div>
                                                    <hr />
                                                  </Fragment>
                                                ),
                                              )}
                                            </>
                                          )}
                                        />
                                      </Fragment>
                                    ))}
                                  </>
                                )}
                              />
                            </Fragment>
                          )}

                          {/* <!-- End RORO --> */}

                          {/* <!-- General Cargo & Bulk Cargo --> */}
                          {/* <!-- Container for clearing --> */}
                          {values.generalContainerType && (
                            <Fragment>
                              <div className="col-sm-12 text-left">
                                <label>
                                  Select Type of Cargo{' '}
                                  {values.category == '2' || values.category == '3' ? (
                                    <span className="text-danger">*</span>
                                  ) : null}
                                </label>

                                <select
                                  className={`form-control ${
                                    getIn(touched, 'generalTypeOfcargo') &&
                                    getIn(errors, 'generalTypeOfcargo')
                                      ? 'is-invalid'
                                      : ''
                                  }`}
                                  value={values.generalTypeOfcargo}
                                  onBlur={handleBlur}
                                  id="generalTypeOfcargo"
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const fields = {
                                      generalTypeOfcargo: value,
                                    };

                                    // Bulk & wet - no forwarding (just like roro)
                                    if (values.category == '3' && value === '1') {
                                      // setting forwarding false because we don't support this type of job
                                      fields.forwardingJob = false;
                                      fields.jobType = true;
                                    }

                                    // to show item name field
                                    if (values.category == '2' && value === '1') {
                                      fields.generalTypeOfcargoSingle = true;
                                    } else if (values.category == '3') {
                                      fields.generalTypeOfcargoSingle = true;
                                    } else {
                                      fields.generalTypeOfcargoSingle = false;
                                    }

                                    // Auto-initialize 1 container when selecting General Cargo type
                                    if (
                                      values.category == '2' &&
                                      value &&
                                      !values.generalNoOfContainer
                                    ) {
                                      fields.generalNoOfContainer = 1;
                                      fields.containers = [
                                        {
                                          item_name: '',
                                          size_of_container: '',
                                          container_type: '',
                                          id: uuidv4(),
                                        },
                                      ];
                                    }

                                    setValues({
                                      ...values,
                                      ...fields,
                                    });
                                  }}
                                >
                                  <option value={''}>Select</option>
                                  {values.category == '2'
                                    ? Object.keys(serviceData.job.type_of_general).map((key) => (
                                        <option value={key} key={key}>
                                          {formatLabel(serviceData.job.type_of_general[key])}
                                        </option>
                                      ))
                                    : Object.keys(serviceData.job.type_of_cargo).map((key) => (
                                        <option value={key} key={key}>
                                          {formatLabel(serviceData.job.type_of_cargo[key])}
                                        </option>
                                      ))}
                                </select>
                                {getIn(touched, 'generalTypeOfcargo') &&
                                getIn(errors, 'generalTypeOfcargo') ? (
                                  <div className="invalid-feedback mb-3">
                                    {getIn(touched, 'generalTypeOfcargo') &&
                                      getIn(errors, 'generalTypeOfcargo')}
                                  </div>
                                ) : null}
                              </div>

                              {values.generalTypeOfcargo && (
                                <>
                                  {values.category === '3' && (
                                    <div className="col-sm-6 text-left">
                                      <label>
                                        Item Name{' '}
                                        {!values.generalTypeOfcargoSingle &&
                                          '(comma separated names)'}
                                        <span className="text-danger">*</span>
                                      </label>

                                      <TextField
                                        value={values.generalItemName}
                                        id="generalItemName"
                                        name="generalItemName"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          getIn(touched, 'generalItemName') &&
                                          getIn(errors, 'generalItemName')
                                        }
                                        type="text"
                                        className="form-control"
                                        placeholder={
                                          values.generalTypeOfcargoSingle
                                            ? 'Item Name'
                                            : 'Item 1, Item 2'
                                        }
                                      />
                                    </div>
                                  )}

                                  {values.generalType ? (
                                    <div className="col-sm-6 text-left">
                                      <label>
                                        Number of Containers{' '}
                                        {values.category == '2' ? (
                                          <span className="text-danger">*</span>
                                        ) : null}
                                      </label>

                                      <TextField
                                        value={values.generalNoOfContainer}
                                        id="generalNoOfContainer"
                                        name="generalNoOfContainer"
                                        onChange={(e) => {
                                          const value = e.target.value
                                            ? parseInt(e.target.value)
                                            : '';
                                          setFieldValue('generalNoOfContainer', value);

                                          if (value >= 1) {
                                            let tempContainer = [];
                                            for (let i = 0; i < value; i++) {
                                              tempContainer.push({
                                                item_name: '',
                                                size_of_container: '',
                                                container_type: '',
                                                id: uuidv4(),
                                              });
                                            }
                                            setFieldValue('containers', tempContainer);
                                          } else {
                                            setFieldValue('containers', []);
                                          }
                                        }}
                                        onBlur={handleBlur}
                                        error={
                                          getIn(touched, 'generalNoOfContainer') &&
                                          getIn(errors, 'generalNoOfContainer')
                                        }
                                        type="number"
                                        className="form-control"
                                        placeholder="Number of containers"
                                        min="0"
                                        onWheel={(e) => e.target.blur()} // to prevent value change when mouse wheel scroll over this field
                                      />
                                    </div>
                                  ) : (
                                    <div className="col-sm-6 text-left">
                                      <label>
                                        Tonnage{' '}
                                        {values.category == '3' ? (
                                          <span className="text-danger">*</span>
                                        ) : null}
                                      </label>
                                      <TextField
                                        value={values.bulkTonnage}
                                        id="bulkTonnage"
                                        name="bulkTonnage"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          getIn(touched, 'bulkTonnage') &&
                                          getIn(errors, 'bulkTonnage')
                                        }
                                        type="text"
                                        className="form-control"
                                        placeholder="Tonnage"
                                      />
                                    </div>
                                  )}

                                  <div className="col-sm-6 text-left">
                                    <label>
                                      Expected Date of Arrival{' '}
                                      <span className="text-danger">*</span>
                                    </label>
                                    <TextField
                                      type="date"
                                      className="form-control"
                                      placeholder=""
                                      value={values.dateOfArrival}
                                      name="dateOfArrival"
                                      id="dateOfArrival"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={
                                        getIn(touched, 'dateOfArrival') &&
                                        getIn(errors, 'dateOfArrival')
                                      }
                                      min={moment().format('YYYY-MM-DD')}
                                    />
                                  </div>

                                  <FieldArray name="containers">
                                    {(arrayHelpers) => (
                                      <>
                                        {values.category === '2' &&
                                          values.generalType &&
                                          values.containers.length > 0 &&
                                          values.containers.map((containerData, index) => (
                                            <Fragment key={index}>
                                              <div className="col-12 mt-3 mb-2">
                                                <label
                                                  className="text-left"
                                                  style={{
                                                    color: '#00828d',
                                                    fontWeight: '600',
                                                    borderBottom: '1px solid #eee',
                                                    paddingBottom: '10px',
                                                    display: 'block',
                                                  }}
                                                >
                                                  {`Container No. ${index + 1}`}
                                                </label>
                                              </div>
                                              <div className="col-sm-6 text-left">
                                                <label>
                                                  Size of Container{' '}
                                                  <span className="text-danger">*</span>
                                                </label>
                                                <select
                                                  className="form-control"
                                                  value={containerData.size_of_container}
                                                  name={`containers.${index}.size_of_container`}
                                                  onChange={handleChange}
                                                  onBlur={handleBlur}
                                                >
                                                  <option value="">Select Size</option>
                                                  <option value="20ft">20ft</option>
                                                  <option value="40ft">40ft</option>
                                                  <option value="45ft">45ft</option>
                                                </select>
                                                <p
                                                  style={{ fontSize: '.875em' }}
                                                  className="text-danger"
                                                >
                                                  <ErrorMessage
                                                    name={`containers.${index}.size_of_container`}
                                                  />
                                                </p>
                                              </div>

                                              {/* Size and type are separate questions: a 40ft box can be Dry
                                                  or Reefer, and each type carries its own tariff. */}
                                              <div className="col-sm-6 text-left">
                                                <label>
                                                  Container Type <span className="text-danger">*</span>
                                                </label>
                                                <select
                                                  className="form-control"
                                                  value={containerData.container_type || ''}
                                                  name={`containers.${index}.container_type`}
                                                  onChange={handleChange}
                                                  onBlur={handleBlur}
                                                >
                                                  <option value="">Select Type</option>
                                                  <option value="Dry">Dry</option>
                                                  <option value="Flat rack">Flat rack</option>
                                                  <option value="High cube">High cube</option>
                                                  <option value="OOG">OOG</option>
                                                  <option value="Reefer">Reefer</option>
                                                </select>
                                                <p style={{ fontSize: '.875em' }} className="text-danger">
                                                  <ErrorMessage name={`containers.${index}.container_type`} />
                                                </p>
                                              </div>
                                              <div className="col-sm-6 text-left">
                                                <label>
                                                  Item Name <span className="text-danger">*</span>
                                                </label>
                                                <TextField
                                                  type="text"
                                                  className="form-control"
                                                  placeholder="Enter item name"
                                                  value={containerData.item_name}
                                                  name={`containers.${index}.item_name`}
                                                  onChange={handleChange}
                                                  onBlur={handleBlur}
                                                  error={
                                                    getIn(touched, 'item_name') &&
                                                    getIn(errors, 'item_name')
                                                  }
                                                />
                                                <p
                                                  style={{ fontSize: '.875em' }}
                                                  className="text-danger"
                                                >
                                                  <ErrorMessage
                                                    name={`containers.${index}.item_name`}
                                                  />
                                                </p>
                                              </div>
                                            </Fragment>
                                          ))}
                                      </>
                                    )}
                                  </FieldArray>
                                </>
                              )}
                            </Fragment>
                          )}

                          {values.clearingJob ? (
                            <div className="col-lg-6 col-sm-6 text-left">
                              <label>
                                Port of Discharge <span className="text-danger">*</span>
                              </label>
                              <select
                                className={`form-control ${
                                  getIn(touched, 'clearingDischarge') &&
                                  getIn(errors, 'clearingDischarge')
                                    ? 'is-invalid'
                                    : ''
                                }`}
                                value={values.clearingDischarge}
                                name="clearingDischarge"
                                id="clearingDischarge"
                                onChange={(e) => {
                                  handleChange(e);
                                  // Terminals are filtered by port, so a
                                  // terminal picked under the previous port
                                  // would no longer be in the list yet would
                                  // still be submitted. Clear it.
                                  if (values.terminal) setFieldValue('terminal', '');
                                }}
                                onBlur={handleBlur}
                              >
                                <option value="">Select Port</option>
                                {(serviceData?.active_ports || serviceData?.ports)?.length > 0 &&
                                  (serviceData?.active_ports || serviceData?.ports).map((obj) => (
                                    <option value={obj.uuid} key={obj.uuid}>
                                      {/* Rendered as stored. startCase(camelCase()) turned
                                          "Ogun II" into "Ogun Ii", "Abuja (FCT)"
                                          into "Abuja Fct" and "Kano/Jigawa" into
                                          "Kano Jigawa" — the same mangling that
                                          was removed from the admin screens. */}
                                      {obj.port_of_operation}
                                    </option>
                                  ))}
                              </select>
                              {getIn(touched, 'clearingDischarge') &&
                              getIn(errors, 'clearingDischarge') ? (
                                <div className="invalid-feedback mb-3">
                                  {getIn(errors, 'clearingDischarge')}
                                </div>
                              ) : null}
                            </div>
                          ) : null}

                          {/* Terminals belong to a port, so only those serving
                              the selected Port of Discharge are offered. If the
                              importer changes the port, a terminal chosen under
                              the old one is cleared rather than left dangling. */}
                          {values.clearingJob ? (
                            <div className="col-lg-6 col-sm-6 text-left">
                              <label>
                                Terminal <span className="text-danger">*</span>
                              </label>
                              <select
                                className={`form-control ${
                                  getIn(touched, 'terminal') && getIn(errors, 'terminal')
                                    ? 'is-invalid'
                                    : ''
                                }`}
                                value={values.terminal}
                                name="terminal"
                                id="terminal"
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <option value="">
                                  {values.clearingDischarge
                                    ? 'Select Terminal'
                                    : 'Select a Port of Discharge first'}
                                </option>
                                {serviceData?.terminals?.length > 0 &&
                                  serviceData.terminals
                                    .filter(
                                      (obj) =>
                                        !values.clearingDischarge ||
                                        obj.port_uuid === values.clearingDischarge,
                                    )
                                    .map((obj) => (
                                      <option value={obj.uuid} key={obj.uuid}>
                                        {obj.terminal_name}
                                      </option>
                                    ))}
                                {/* Last, so it reads as the fallback it is. The
                                    seeded list is never complete — a new bonded
                                    terminal opens, and the importer would
                                    otherwise be unable to post the job at all. */}
                                <option value={OTHER_OPTION}>Others (type it in)</option>
                              </select>
                              {getIn(touched, 'terminal') && getIn(errors, 'terminal') ? (
                                <div className="invalid-feedback mb-3">
                                  {getIn(errors, 'terminal')}
                                </div>
                              ) : null}

                            </div>
                          ) : null}

                          {/* Its own column rather than tucked under the select,
                              so it takes a grid slot like any other field and
                              the row stays even at every breakpoint. */}
                          {values.clearingJob && values.terminal === OTHER_OPTION ? (
                            <div className="col-lg-6 col-sm-6 text-left">
                              <label>
                                Terminal Name <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${
                                  getIn(touched, 'terminalOther') && getIn(errors, 'terminalOther')
                                    ? 'is-invalid'
                                    : ''
                                }`}
                                name="terminalOther"
                                id="terminalOther"
                                maxLength={120}
                                placeholder="Enter the terminal name"
                                value={values.terminalOther}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              {getIn(touched, 'terminalOther') &&
                              getIn(errors, 'terminalOther') ? (
                                <div className="invalid-feedback mb-3">
                                  {getIn(errors, 'terminalOther')}
                                </div>
                              ) : null}
                            </div>
                          ) : null}

                          {values.clearingJob ? (
                            <div className="col-lg-6 col-sm-6 text-left">
                              <label>
                                Shipping Line <span className="text-danger">*</span>
                              </label>
                              <select
                                className={`form-control ${
                                  getIn(touched, 'shippingLine') && getIn(errors, 'shippingLine')
                                    ? 'is-invalid'
                                    : ''
                                }`}
                                value={values.shippingLine}
                                name="shippingLine"
                                id="shippingLine"
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <option value="">Select Shipping Line</option>
                                {serviceData?.shipping_lines?.length > 0 &&
                                  serviceData.shipping_lines.map((obj) => (
                                    <option value={obj.uuid} key={obj.uuid}>
                                      {obj.shipping_line_name}
                                    </option>
                                  ))}
                                <option value={OTHER_OPTION}>Others (type it in)</option>
                              </select>
                              {getIn(touched, 'shippingLine') && getIn(errors, 'shippingLine') ? (
                                <div className="invalid-feedback mb-3">
                                  {getIn(errors, 'shippingLine')}
                                </div>
                              ) : null}

                            </div>
                          ) : null}

                          {values.clearingJob && values.shippingLine === OTHER_OPTION ? (
                            <div className="col-lg-6 col-sm-6 text-left">
                              <label>
                                Shipping Line Name <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${
                                  getIn(touched, 'shippingLineOther') &&
                                  getIn(errors, 'shippingLineOther')
                                    ? 'is-invalid'
                                    : ''
                                }`}
                                name="shippingLineOther"
                                id="shippingLineOther"
                                maxLength={120}
                                placeholder="Enter the shipping line name"
                                value={values.shippingLineOther}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              {getIn(touched, 'shippingLineOther') &&
                              getIn(errors, 'shippingLineOther') ? (
                                <div className="invalid-feedback mb-3">
                                  {getIn(errors, 'shippingLineOther')}
                                </div>
                              ) : null}
                            </div>
                          ) : null}

                          {/* Telex Release means the shipping line has already
                              released the cargo electronically, so no Bill of
                              Lading is uploaded for the job. */}
                          {values.clearingJob ? (
                            <div className="col-lg-6 col-sm-6 text-left">
                              <label>
                                Mode of Release <span className="text-danger">*</span>
                              </label>
                              <select
                                className={`form-control ${
                                  getIn(touched, 'modeOfRelease') && getIn(errors, 'modeOfRelease')
                                    ? 'is-invalid'
                                    : ''
                                }`}
                                value={values.modeOfRelease}
                                name="modeOfRelease"
                                id="modeOfRelease"
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <option value="">Select Mode of Release</option>
                                {serviceData?.job?.mode_of_release &&
                                  Object.keys(serviceData.job.mode_of_release).map((key) => (
                                    <option value={key} key={key}>
                                      {serviceData.job.mode_of_release[key]}
                                    </option>
                                  ))}
                              </select>
                              {getIn(touched, 'modeOfRelease') && getIn(errors, 'modeOfRelease') ? (
                                <div className="invalid-feedback mb-3">
                                  {getIn(errors, 'modeOfRelease')}
                                </div>
                              ) : null}
                            </div>
                          ) : null}

                          {/* <div className="col-lg-6 col-sm-6 text-left">
                            <label>Pick-up Location</label>
                            <select
                              className={`form-control ${
                                getIn(touched, 'forwardingLocation') &&
                                getIn(errors, 'forwardingLocation')
                                  ? 'is-invalid'
                                  : ''
                              }`}
                              value={values.forwardingLocation}
                              name="forwardingLocation"
                              id="forwardingLocation"
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option value="">Select Port</option>
                              {(serviceData?.active_ports || serviceData?.ports)?.length > 0 &&
                                (serviceData?.active_ports || serviceData?.ports).map((obj) => (
                                  <option value={obj.uuid} key={obj.uuid}>
                                    {obj.port_of_operation}
                                  </option>
                                ))}
                            </select>
                            {getIn(touched, 'forwardingLocation') &&
                            getIn(errors, 'forwardingLocation') ? (
                              <div className="invalid-feedback mb-3">
                                {getIn(touched, 'forwardingLocation') &&
                                  getIn(errors, 'forwardingLocation')}
                              </div>
                            ) : null}
                          </div> */}
                          {/* Transport Job */}
                          {values.forwardingJob && (
                            <div style={{ marginTop: '20px' }}>
                              {/* <h3 className="text-lg-start">Transport Services</h3> */}

                              {hasNoForwardingJob ? (
                                <p>
                                  Note: Sorry we don't provide transport services for this type of
                                  job!
                                </p>
                              ) : (
                                <Fragment>
                                  {/* <div className="col-lg-6 col-sm-6 text-left">
                                    <label>Port of discharge</label>
                                    <select
                                      className={`form-control ${
                                        getIn(touched, 'forwardingLocation') &&
                                        getIn(errors, 'forwardingLocation')
                                          ? 'is-invalid'
                                          : ''
                                      }`}
                                      value={values.forwardingLocation}
                                      name="forwardingLocation"
                                      id="forwardingLocation"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                    >
                                      <option value="">Select Port</option>
                                      {(serviceData?.active_ports || serviceData?.ports)?.length > 0 &&
                                        (serviceData?.active_ports || serviceData?.ports).map((obj) => (
                                          <option value={obj.uuid} key={obj.uuid}>
                                            {obj.port_of_operation}
                                          </option>
                                        ))}
                                    </select>
                                    {getIn(touched, 'forwardingLocation') &&
                                    getIn(errors, 'forwardingLocation') ? (
                                      <div className="invalid-feedback mb-3">
                                        {getIn(touched, 'forwardingLocation') &&
                                          getIn(errors, 'forwardingLocation')}
                                      </div>
                                    ) : null}
                                  </div> */}

                                  <div className="col-lg-6 col-sm-6 text-left">
                                    <label>Pick-up Location</label>
                                    <select
                                      className={`form-control ${
                                        getIn(touched, 'forwardingLocation') &&
                                        getIn(errors, 'forwardingLocation')
                                          ? 'is-invalid'
                                          : ''
                                      }`}
                                      value={values.forwardingLocation}
                                      name="forwardingLocation"
                                      id="forwardingLocation"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                    >
                                      <option value="">Select Port</option>
                                      {(serviceData?.active_ports || serviceData?.ports)?.length > 0 &&
                                        (serviceData?.active_ports || serviceData?.ports).map((obj) => (
                                          <option value={obj.uuid} key={obj.uuid}>
                                            {obj.port_of_operation}
                                          </option>
                                        ))}
                                    </select>
                                    {getIn(touched, 'forwardingLocation') &&
                                    getIn(errors, 'forwardingLocation') ? (
                                      <div className="invalid-feedback mb-3">
                                        {getIn(touched, 'forwardingLocation') &&
                                          getIn(errors, 'forwardingLocation')}
                                      </div>
                                    ) : null}
                                  </div>

                                  <div className="col-lg-6 col-sm-6 text-left">
                                    <label>Destination</label>
                                    <Autocomplete
                                      placeholder="Full Address"
                                      value={values.forwardingDestination}
                                      options={{
                                        types: [],
                                        componentRestrictions: {
                                          country: 'ng',
                                        },
                                      }}
                                      onChange={(e) =>
                                        setFieldValue('forwardingDestination', e.target.value)
                                      }
                                      onBlur={handleBlur}
                                      apiKey={config.GOOGLE_API_KEY}
                                      onPlaceSelected={(place) => {
                                        setFieldValue(
                                          'forwardingDestination',
                                          place.formatted_address,
                                        );
                                      }}
                                      className={`form-control ${
                                        getIn(touched, 'forwardingDestination') &&
                                        getIn(errors, 'forwardingDestination')
                                          ? 'is-invalid'
                                          : ''
                                      }`}
                                    />{' '}
                                    {getIn(touched, 'forwardingDestination') &&
                                    getIn(errors, 'forwardingDestination') ? (
                                      <div className="invalid-feedback mb-3">
                                        {getIn(touched, 'forwardingDestination') &&
                                          getIn(errors, 'forwardingDestination')}
                                      </div>
                                    ) : null}
                                  </div>
                                  <div className="col-lg-6 col-sm-6 text-left">
                                    <label>State</label>
                                    <select
                                      type="text"
                                      className="form-control"
                                      placeholder="Year"
                                      value={values.forwardingDestinationState}
                                      name="forwardingDestinationState"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={getIn(touched, 'year') && getIn(errors, 'year')}
                                    >
                                      <option>Select State</option>
                                      {serviceData.states?.length > 0 &&
                                        serviceData.states.map((state) => (
                                          <option value={state.uuid} key={state.uuid}>
                                            {state.state}
                                          </option>
                                        ))}
                                    </select>
                                    {getIn(touched, 'forwardingDestinationState') &&
                                    getIn(errors, 'forwardingDestinationState') ? (
                                      <div className="invalid-feedback mb-3">
                                        {getIn(touched, 'forwardingDestinationState') &&
                                          getIn(errors, 'forwardingDestinationState')}
                                      </div>
                                    ) : null}
                                  </div>
                                  <div className="col-lg-6 col-sm-12 text-left mb-4">
                                    {values.performa_invoice && values.jobId && (
                                      <div className="text-left">
                                        <span
                                          style={{ color: '#337ab7', cursor: 'pointer' }}
                                          onClick={() => showDoc(values.performa_invoice)}
                                        >
                                          <i className="fa fa-download"></i>Proforma Invoice
                                        </span>
                                      </div>
                                    )}
                                    <label style={{ marginBottom: '10px' }}>
                                      Upload Proforma Invoice
                                    </label>

                                    <input
                                      type="file"
                                      accept={acceptedFileTypes}
                                      className={` ${
                                        getIn(touched, 'performa_invoice') &&
                                        getIn(errors, 'performa_invoice')
                                          ? 'is-invalid'
                                          : ''
                                      }`}
                                      name="performa_invoice"
                                      id="performa_invoice"
                                      onChange={(e) => {
                                        const file = e.target.files[0];

                                        if (file) {
                                          setFieldValue('performa_invoice', file);
                                        }
                                      }}
                                      onBlur={handleBlur}
                                    />
                                    {getIn(touched, 'performa_invoice') &&
                                    getIn(errors, 'performa_invoice') ? (
                                      <div className="invalid-feedback mb-3">
                                        {getIn(touched, 'performa_invoice') &&
                                          getIn(errors, 'performa_invoice')}
                                      </div>
                                    ) : null}
                                  </div>
                                </Fragment>
                              )}
                            </div>
                          )}

                          <div className="col-sm-12">
                            <div className="buttonsec">
                              <button
                                type="button"
                                className="submitbut btn-light"
                                onClick={() => {
                                  props.history.goBack();
                                }}
                              >
                                Back
                              </button>
                              <button
                                type="submit"
                                className="submitbut"
                                disabled={values.forwardingJob && hasNoForwardingJob}
                              >
                                {values.jobId ? 'Update Job' : 'Submit'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                );
              }}
            </Formik>
          </section>
        </section>
      </div>
      {/* Modal to Display Document */}
      {documentToShow && (
        <DocumentModal
          isOpen={documentToShow}
          toggle={() => setDocumentToShow('')}
          documentToShow={documentToShow}
          container="client-page"
        />
      )}
    </div>
  );
};

export default CreateServices;
